import { enableProdMode, SecurityContext, isDevMode, inject } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';

import { MARKED_OPTIONS, MarkedOptions, MarkedRenderer, provideMarkdown } from 'ngx-markdown';
import { HOME_HUB_CONFIG } from '@crewdle/home-hub/angular';
import { PRODUCT_CATALOG_CONFIG } from '@crewdle/product-catalog/angular';
import { applyCachedBrand } from '@crewdle/white-label';
import { WHITE_LABEL_CONFIG } from '@crewdle/white-label/angular';

import { routes } from 'src/app/app.routes';
import { AppComponent } from 'src/app/app.component';
import { PRODUCT_ID } from 'src/app/config/product';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';

import { environment } from 'src/environments/environment';
import { provideHttpClient } from '@angular/common/http';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco } from '@jsverse/transloco';
import { provideServiceWorker } from '@angular/service-worker';

if (environment.production) {
  enableProdMode();
}

// Re-apply the last-known partner brand from localStorage before Angular
// renders anything, so branded users don't see a flash of Crewdle defaults.
// The live Firestore read then confirms or replaces it.
applyCachedBrand();

export function markedOptionsFactory(): MarkedOptions {
  const renderer = new MarkedRenderer();
  const linkRenderer = renderer.link;
  const codeRenderer = renderer.code;

  renderer.link = (href, title, text) => {
    const html = linkRenderer.call(renderer, href, title, text);
    return html.replace(/^<a /, '<a role="link" tabindex="0" target="_blank" rel="nofollow noopener noreferrer" ');
  };

  renderer.code = (code, language, isEscaped) => {
    let html = codeRenderer.call(renderer, code, language, isEscaped);

    if (language === 'html') {
      const escapedCode = code
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

      const toggleButtons = `
        <div class="code-block-buttons-container">
          <div class="code-block-buttons">
            <button class="code-block-button" onclick="this.parentElement.parentElement.nextElementSibling.style.display = 'block'; this.nextElementSibling.style.display = 'block'; this.parentElement.parentElement.nextElementSibling.nextElementSibling.style.display = 'none'; this.style.display = 'none';" style="display: block">Preview HTML</button>
            <button class="code-block-button" onclick="this.parentElement.parentElement.nextElementSibling.nextElementSibling.style.display = 'block'; this.previousElementSibling.style.display = 'block'; this.parentElement.parentElement.nextElementSibling.style.display = 'none'; this.style.display = 'none';" style="display: none">View Code</button>
          </div>
        </div>
      `

      html = `${toggleButtons}<iframe srcdoc="${escapedCode}"></iframe>${html}`;
    }

    return html;
  };

  return {
    renderer,
    gfm: true,
    breaks: false,
    pedantic: false,
  };
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(routes),
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: markedOptionsFactory,
      },
      sanitize: SecurityContext.NONE,
    }),
    provideHttpClient(),
    {
      // Product catalog reader config (Firestore-driven cross-product switcher).
      provide: PRODUCT_CATALOG_CONFIG,
      useFactory: () => ({
        firestore: inject(FirebaseService).firestore,
        domain: environment.production ? 'crewdle.ai' : 'stg.crewdle.ai',
        currentProductId: PRODUCT_ID,
        includeDevelopment: !environment.production,
      }),
    },
    {
      // Partner white-label branding (colors, logos, favicon, title).
      provide: WHITE_LABEL_CONFIG,
      useFactory: () => ({
        firestore: inject(FirebaseService).firestore,
      }),
    },
    {
      // Cross-product home hub (full-screen launcher opened from the sidebar).
      provide: HOME_HUB_CONFIG,
      useFactory: () => ({
        firestore: inject(FirebaseService).firestore,
        domain: environment.production ? 'crewdle.ai' : 'stg.crewdle.ai',
      }),
    },
    provideTransloco({
      config: {
        availableLangs: ['en', 'fr'],
        defaultLang: (() => {
          const saved = localStorage.getItem('lang');
          if (saved === 'en' || saved === 'fr') return saved;
          try {
            const cookies = document.cookie.split(';');
            for (const c of cookies) {
              const t = c.trim();
              if (t.startsWith('crewdle_lang=')) {
                const v = decodeURIComponent(t.substring('crewdle_lang='.length));
                if (v === 'en' || v === 'fr') return v;
              }
            }
          } catch {}
          return navigator.language.startsWith('fr') ? 'fr' : 'en';
        })(),
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
  ],
});
